import { useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowRight, MessageCircle, X } from "lucide-react";
import { PageHero, FadeUp } from "../components/Reveal";
import { MORE_IN_STORE } from "../lib/content";

const EnquiryBox = ({ subName = "" }) => (
  <FadeUp>
    <div className="border border-gold/30 bg-ink-2/60 px-8 py-14 lg:py-16 text-center" data-testid="instore-enquiry-box">
      <MessageCircle className="w-5 h-5 text-gold mx-auto mb-6" />
      <h2 className="font-serif text-3xl text-ivory font-light">{MORE_IN_STORE.enquiryHeading}</h2>
      <p className="text-stone-mist font-light mt-4 max-w-xl mx-auto leading-relaxed">{MORE_IN_STORE.enquiryText}</p>
      <Link
        to={`/contact?subject=${encodeURIComponent(`In-store enquiry${subName ? ` — ${subName}` : ""}`)}`}
        data-testid="make-an-enquiry-button"
        className="inline-flex items-center gap-2 bg-gold text-ink px-10 py-4 text-xs uppercase tracking-[0.25em] font-medium mt-8 hover:bg-gold-glow transition-colors duration-300"
      >
        Make an Enquiry <ArrowRight className="w-3.5 h-3.5" />
      </Link>
    </div>
  </FadeUp>
);

export default function MoreInStore() {
  const { sub } = useParams();
  const [lightbox, setLightbox] = useState(null);
  const active = MORE_IN_STORE.subcategories.find((s) => s.slug === sub) || null;

  if (sub && !active) return <Navigate to="/more-in-store" replace />;

  // Subcategory showcase gallery — visual inspiration only, no products/prices/stock
  if (active) {
    return (
      <main data-testid={`more-in-store-${active.slug}`}>
        <PageHero eyebrow="More In Store" title={active.name} sub={active.description} />
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
          <p className="text-stone-mist text-sm font-light mb-10 max-w-2xl">
            A taste of what we currently have in our Peel shop — this range changes weekly, so every visit brings something new.
          </p>
          <div className="columns-2 md:columns-3 lg:columns-4 gap-5" data-testid="instore-gallery">
            {active.gallery.map((src, i) => (
              <FadeUp key={src} delay={(i % 4) * 0.06} className="mb-5 break-inside-avoid">
                <button
                  onClick={() => setLightbox(src)}
                  data-testid={`instore-image-${active.slug}-${i}`}
                  className="group block w-full overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700 focus:outline-none"
                  aria-label={`${active.name} showcase image ${i + 1}`}
                >
                  <img
                    src={src}
                    alt={`${active.name} available in the Celtic Gold shop, Peel`}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
                  />
                </button>
              </FadeUp>
            ))}
          </div>
          <div className="mt-16">
            <EnquiryBox subName={active.name} />
          </div>
        </section>

        {lightbox && (
          <div
            className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setLightbox(null)}
            data-testid="instore-lightbox"
          >
            <button className="absolute top-6 right-6 text-ivory hover:text-gold transition-colors" aria-label="Close" data-testid="instore-lightbox-close">
              <X className="w-6 h-6" />
            </button>
            <img src={lightbox} alt="" className="max-h-[85vh] max-w-full object-contain border border-gold/20" />
          </div>
        )}
      </main>
    );
  }

  // Landing: prominent intro + the three showcase collections
  return (
    <main data-testid="more-in-store">
      <PageHero eyebrow="Celtic Gold · Peel" title="More In Store" />
      <section className="max-w-3xl mx-auto px-6 text-center pb-20" data-testid="instore-intro">
        <FadeUp>
          <h2 className="font-serif text-3xl sm:text-4xl text-gold font-light">{MORE_IN_STORE.introTitle}</h2>
          <div className="space-y-5 mt-8">
            {MORE_IN_STORE.intro.map((p, i) => (
              <p key={i} className="text-ivory/85 font-light leading-relaxed">{p}</p>
            ))}
          </div>
        </FadeUp>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {MORE_IN_STORE.subcategories.map((s, i) => (
            <FadeUp key={s.slug} delay={(i % 3) * 0.08}>
              <Link
                to={`/more-in-store/${s.slug}`}
                data-testid={`subcategory-card-${s.slug}`}
                className="group relative block h-96 overflow-hidden border border-white/5 hover:border-gold/40 transition-colors duration-700"
              >
                <img src={s.cover} alt={s.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1.4s] group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/35 to-transparent" />
                <div className="absolute bottom-0 p-7">
                  <h3 className="font-serif text-2xl text-ivory group-hover:text-gold transition-colors duration-500">{s.name}</h3>
                  <p className="text-stone-mist text-sm font-light mt-2 max-w-xs">{s.tagline}</p>
                  <span className="inline-flex items-center gap-2 text-gold text-[11px] uppercase tracking-[0.25em] mt-5">
                    Take a Look <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
        <div className="mt-16">
          <EnquiryBox />
        </div>
      </section>
    </main>
  );
}
